import dayjs from "dayjs";
import { useState } from "react";

import { FormModal, PlainDateField, notify } from "@/components/kit";
import { useRescheduleDelivery } from "@/hooks/api/order/useRescheduleDelivery";
import { getApiErrorMessage } from "@/utils/api-error";
import { formatDay } from "@/utils/date";

const DUBAI_TIME_ZONE = "Asia/Dubai";

const dubaiDay = (date: string | Date) =>
  dayjs(date).tz(DUBAI_TIME_ZONE).format("YYYY-MM-DD");

interface RescheduleDeliveryModalProps {
  isOpen: boolean;
  orderId: string;
  displayId: string;
  deliveryDate: string | null;
  onClose: () => void;
}

export const RescheduleDeliveryModal = ({
  isOpen,
  orderId,
  displayId,
  deliveryDate,
  onClose,
}: RescheduleDeliveryModalProps) => {
  const initialDay = deliveryDate ? dubaiDay(deliveryDate) : "";
  const [day, setDay] = useState(initialDay);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { mutate: reschedule, isPending } = useRescheduleDelivery();

  const today = dubaiDay(new Date());
  const error = isSubmitted && day === "" ? "Pick a delivery day" : null;

  const handleSubmit = () => {
    setIsSubmitted(true);

    if (day === "" || day < today) return;

    reschedule(
      { orderId, deliveryDate: day },
      {
        onSuccess: () => {
          notify.success(
            "Delivery date changed",
            `Order #${displayId} now arrives on ${formatDay(day)}`,
          );
          onClose();
        },
        onError: (mutationError) =>
          notify.error(
            "Failed to change delivery date",
            getApiErrorMessage(mutationError, "Please try again"),
          ),
      },
    );
  };

  return (
    <FormModal
      isOpen={isOpen}
      isSubmitDisabled={day === initialDay}
      isSubmitting={isPending}
      submitLabel="Change date"
      title={`Change delivery date for #${displayId}`}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <PlainDateField
        description="Dubai time. The customer gets a push notification with the new day."
        label="Delivery day"
        minValue={today}
        name="deliveryDate"
        value={day}
        onChange={setDay}
      />
      {error ? (
        <p className="text-sm text-(--danger)" role="alert">
          {error}
        </p>
      ) : null}
    </FormModal>
  );
};
